// const id = localStorage.getItem("companyId") ?? "";
// const parentId = id.replace(/^"(.+)"$/, "$1") ?? "";

import { useState, useEffect } from "react";

// Third Party
import { Formik, Field } from "formik";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient, useQueries } from "@tanstack/react-query";
import moment from "moment";

// UI
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import DatePicker from "@/components/ui/DatePicker";
import { useToast } from "@/components/ui/use-toast";

// Icons
import { ArrowLeft, Upload, Search, Save } from "lucide-react";

// Types and Constant
import type { MutateSOE, SOE } from "@/types/soe";
import type {
	Provinces,
	Ministry,
	Department,
	EconomicRegion,
	CoreSector,
	Sector,
} from "@/types/master";
import { createSubSOE as createSOE, updateSOE } from "@/services/soe";
import { handleUploadFile } from "@/services/presign-url";
import { S3_BUCKET_LINK } from "@/components/constants";
import {
	getProvinces,
	getMinistries,
	getDepartments,
	getEconomicRegions,
	getCoreSectors,
	getSectors,
} from "@/services/master";
import type { TaxRISResponse } from "@/types/third-party";
import { getSOEByTaxRis } from "@/services/third-party";

export const Route = createFileRoute(
	"/_layout-private/account-management/soe/_information/joint-venture/mutate",
)({
	component: CreateSubSOE,
	validateSearch: (search) => {
		return {
			childId: search.childId as string | undefined,
			data: search.data as string | undefined,
		};
	},
});

function CreateSubSOE() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const id = localStorage.getItem("companyId") ?? "";
	const parentId = id.replace(/^"(.+)"$/, "$1") ?? "";

	// state
	const [parsedData, setParsedData] = useState({} as SOE);

	const [selectedFile, setSelectedFile] = useState("");

	const [taxpayerId, setTaxpayerId] = useState("");
	const [taxRisData, setTaxRisData] = useState({} as TaxRISResponse);

	// Query
	const [
		{ data: provincesRes },
		{ data: departmentsRes },
		{ data: ministriesRes },
		{ data: economicRegionsRes },
		{ data: coreSectorsRes },
		{ data: sectorRes },
	] = useQueries({
		queries: [
			{
				queryKey: ["provinces"],
				queryFn: getProvinces,
			},
			{
				queryKey: ["departments"],
				queryFn: () => getDepartments(),
			},
			{
				queryKey: ["ministries"],
				queryFn: () => getMinistries(),
			},
			{
				queryKey: ["economic-regions"],
				queryFn: () => getEconomicRegions(),
			},
			{
				queryKey: ["core-sector"],
				queryFn: () => getCoreSectors(),
			},
			{
				queryKey: ["sector"],
				queryFn: () => getSectors(),
			},
		],
	});

	// Mutations
	const getTaxRisMutation = useMutation<
		unknown,
		unknown,
		{ taxpayerId: string }
	>({
		mutationFn: (formData) => getSOEByTaxRis(formData),
		onSuccess: (data) => {
			console.log("TAXRIS RES", data);

			const tinResult = (data as any)?.data?.TinInfo[0];

			if (tinResult) {
				toast({
					title: "ສຳເລັດ",
					description: "ຄົ້ນຫາເລກປະຈຳຕົວຜູ້ເສຍອາກອນສຳເລັດ",
				});

				console.log("tin result", tinResult);

				setTaxRisData(tinResult);

				return;
			}
		},
		onError: () => {
			setTaxRisData({} as TaxRISResponse);

			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ບໍ່ພົບຂໍ້ມູນທີ່ຄົ້ນຫາ",
				variant: "destructive",
			});
		},
	});

	// Effects
	useEffect(() => {
		if (search.data) {
			const parseData = JSON.parse(search.data);

			console.log(JSON.parse(search.data), "Data");
			setParsedData(parseData);

			if (parseData?.taxpayerId) {
				getTaxRisMutation.mutate({ taxpayerId: parseData?.taxpayerId });
				setTaxpayerId(parseData?.taxpayerId);
			}
		}
	}, [search]);

	// Mutations
	const createSOEMutation = useMutation<unknown, unknown, MutateSOE>({
		mutationFn: (formData) => createSOE(formData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["joint-venture"] });
			toast({
				title: "ສຳເລັດ",
				description: "ເພີ່ມບັນຊີວິສາຫະກິດສໍາເລັດແລ້ວ",
			});
			navigate({ to: "..", replace: true });
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	const updateSOEMutation = useMutation<unknown, unknown, MutateSOE>({
		mutationFn: (formData) => updateSOE(parsedData?.id, formData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["joint-venture"] });
			toast({
				title: "ສຳເລັດ",
				description: "ແກ້ໄຂບັນຊີວິສາຫະກິດສໍາເລັດແລ້ວ",
			});
			navigate({ to: "..", replace: true });
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	const uploadMutation = useMutation({
		mutationFn: handleUploadFile,
		onSuccess: (filename) => {
			console.log("Upload successful, filename:", filename);

			setSelectedFile(filename);

			toast({
				title: "ສຳເລັດ",
				description: "ອັບໂຫຼດຂໍ້ມູນສໍາເລັດແລ້ວ",
			});
		},
		onError: (error) => {
			console.error("Upload failed:", error);

			const fileInput = document.getElementById("fileUrl") as HTMLInputElement;
			if (fileInput) fileInput.value = "";

			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
				variant: "destructive",
			});
		},
	});

	// Data
	const province = provincesRes?.data as Provinces[];
	const department = departmentsRes?.data as Department[];
	const ministry = ministriesRes?.data as Ministry[];
	const economicRegion = economicRegionsRes?.data as EconomicRegion[];
	const coreSector = coreSectorsRes?.data as CoreSector[];
	const sector = sectorRes?.data as Sector[];

	console.log(sector);

	const mutationLoading =
		createSOEMutation.isPending || updateSOEMutation.isPending;

	return (
		<>
			<section className="mt-[32px]">
				<h2 className="text-2xl font-semibold">ເພີ່ມບັນຊີບໍລິສັດຍ່ອຍ / ໃນເຄືອ</h2>
				<p className="text-muted-foreground">
					ປ້ອນຂໍ້ມູນກ່ຽວກັບກົມ ຫຼື ພະແນກລັດວິສາຫະກິດຄຸ້ມຄອງການລົງທຶນ ແລະ ປະກັນໄພ
				</p>
				<Separator className="my-6" />
				<button
					type="button"
					onClick={() =>
						navigate({
							to: "..",
						})
					}
					className="flex items-center gap-[4px]"
				>
					<ArrowLeft className="w-5 h-5" />
					ກັບຄືນ
				</button>
			</section>
			<Formik
				enableReinitialize
				initialValues={{
					parentCompanyId: parentId,
					taxpayerId: undefined,
					registrationId: undefined,
					economicRegionId: undefined,
					companyName: taxRisData?.CO_NM,

					establishedDate: taxRisData?.ERC_ISSU_DD
						? moment(taxRisData.ERC_ISSU_DD, "DD/MM/YYYY").format("YYYY-MM-DD")
						: undefined,
					businessType: undefined,

					coreBusiness: taxRisData?.BUSN_TP_NM,
					// coreSectorId: undefined,
					// sectorId: undefined,
					purpose: undefined,
					partyCommittee: undefined,
					govShareholding: undefined,
					domShareholding: 0,
					forShareholding: 0,
					registeredCapital: 0,
				}}
				onSubmit={(values: MutateSOE) => {
					// const filteredAddOnValue = Object.fromEntries(
					// 	Object.entries(addOnValue).filter(([, value]) => value !== ""),
					// );

					// console.log(addOnValue, "Prep Addon");

					const prepareData = {
						...values,
						taxpayerId: taxpayerId,
						companyProfileUrl: selectedFile,
						isManagedSubsidiary: true,
					};

					parsedData?.id
						? updateSOEMutation.mutate(prepareData)
						: createSOEMutation.mutate(prepareData);

					// createSOEMutation.mutate(prepareData);
				}}
			>
				{({ values, handleSubmit, setFieldValue }) => (
					<form onSubmit={handleSubmit} className="mt-6 space-y-6">
						<div>
							<Label>
								ເລກປະຈຳຕົວຜູ້ເສຍອາກອນ <span className="text-red-500">*</span>
							</Label>
							<div className="flex items-center gap-2">
								<Input
									name="taxpayerId"
									placeholder="ເລກປະຈຳຕົວຜູ້ເສຍອາກອນ"
									type="text"
									value={taxpayerId}
									onChange={(e) => setTaxpayerId(e.target.value)}
									required
									disabled={parsedData?.taxpayerId ? true : false}
								/>
								<Button
									type="button"
									onClick={() => getTaxRisMutation.mutateAsync({ taxpayerId })}
									disabled={getTaxRisMutation.isPending}
								>
									{getTaxRisMutation.isPending ? (
										<Spinner className="text-white" />
									) : (
										<>
											<Search />
											ຄົ້ນຫາ
										</>
									)}
								</Button>
							</div>
						</div>

						{taxRisData?.CO_NM ? (
							<>
								<section className="space-y-6">
									<header>
										<h3 className="text-[18px] font-semibold">ຂໍ້ມູນທົ່ວໄປ</h3>
										<p className="text-muted-foreground text-[14px]">
											ສິ່ງທີ່ທ່ານປ້ອນໄປຈະຖືກນຳໄປບັນທຶກເປັນຂໍ້ມູນບຸກຄົນ
										</p>
										<Separator className="mt-6" />
									</header>

									<div>
										<Label htmlFor="profile_image_url">ຮູບພາບ</Label>
										<div className="w-[200px] h-[200px] rounded-full border border-dotted bg-zinc-50 border-zinc-200 overflow-hidden  relative flex items-center flex-col justify-center gap-[24px]">
											{selectedFile ? (
												<img
													alt="pre "
													src={S3_BUCKET_LINK + selectedFile}
													className="object-cover w-full h-full"
												/>
											) : uploadMutation.isPending ? (
												<p>ກຳລັງອັບໂຫຼດຮູບພາບ...</p>
											) : (
												<>
													<Upload className="w-8 h-8" />
													<div className="text-center text-[14px]">
														<p>ອັບໂຫຼດຮູບພາບ</p>
														<p className="w-full text-muted-foreground text-wrap">
															(ຂະໜາດບໍ່ເກີນ 10MB)
														</p>
													</div>{" "}
												</>
											)}

											<input
												type="file"
												name="fileUrl"
												id="fileUrl"
												accept="image/png, image/jpg, image/jpeg"
												className="absolute inset-0 opacity-0 cursor-pointer"
												onChange={(e) => {
													if (e.target.files) {
														uploadMutation.mutate(e);
													}
												}}
											/>
										</div>
									</div>

									<div>
										<Label>
											ເລກທະບຽນວິສາຫະກິດ <span className="text-red-500">*</span>
										</Label>
										<Field
											as={Input}
											name="registrationId"
											placeholder="ເລກທະບຽນວິສາຫະກິດ"
											type="text"
											required
										/>
									</div>
									<div>
										<Label>
											ຊື່ວິສາຫະກິດ <span className="text-red-500">*</span>
										</Label>
										<Field
											as={Input}
											name="companyName"
											placeholder="ຊື່ວິສາຫະກິດ"
											type="text"
											required
											disabled
										/>
									</div>
									<DatePicker
										label="ວັນທີສ້າງຕັ້ງ"
										name="establishedDate"
										id="establishedDate"
										value={values.establishedDate as string}
										onChange={(value) => {
											setFieldValue("establishedDate", value);
										}}
										disabled={taxRisData?.ERC_ISSU_DD ? true : false}
									/>

									<div>
										<Label htmlFor="economicRegionId">ເປົ້າໝາຍຍຸດທະສາດດ້ານ</Label>
										<Select
											name="economicRegionId"
											onValueChange={(value) => {
												setFieldValue("economicRegionId", value);
											}}
											value={values.economicRegionId}
										>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="ເລືອກເປົ້າໝາຍຍຸດທະສາດດ້ານ" />
											</SelectTrigger>
											<SelectContent>
												{economicRegion?.map((item) => (
													<SelectItem value={item.id} key={item.id}>
														{item?.economicRegion}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									<div>
										<Label>ຈຸດປະສົງ</Label>
										<Field
											as={Input}
											name="purpose"
											placeholder="ປ້ອນຈຸດປະສົງ..."
											type="text"
										/>
									</div>
									<div>
										<Label>ມີຮາກຖານຂຶ້ນກັບອົງຄະນະພັກ</Label>
										<Field
											as={Input}
											name="partyCommittee"
											placeholder="ປ້ອນມີຮາກຖານຂຶ້ນກັບອົງຄະນະພັກ..."
											type="text"
										/>
									</div>
								</section>

								<section className="space-y-6">
									<header>
										<h3 className="text-[18px] font-semibold">ພາກສ່ວນການຖືຫຸ້ນ</h3>
										<p className="text-muted-foreground text-[14px]">
											ຂໍ້ມູນການຖືຫຸ້ນຂອງວິສາຫະກິດ.
										</p>
										<Separator className="mt-6" />
									</header>

									<div>
										<Label>
											ລັດຖືຫຸ້ນ <span className="text-red-500">*</span>
										</Label>
										<Field
											as={Input}
											name="govShareholding"
											placeholder="ປ້ອນລັດຖືຫຸ້ນ..."
											type="number"
											required
										/>
									</div>
									<div>
										<Label>ເອກະຊົນພາຍໃນຖືຫຸ້ນ</Label>
										<Field
											as={Input}
											name="domShareholding"
											placeholder="ປ້ອນເອກະຊົນພາຍໃນຖືຫຸ້ນ..."
											type="number"
										/>
									</div>
									<div>
										<Label>ເອກະຊົນພາຍນອກຖືຫຸ້ນ</Label>
										<Field
											as={Input}
											name="forShareholding"
											placeholder="ປ້ອນເອກະຊົນພາຍນອກຖືຫຸ້ນ..."
											type="number"
										/>
									</div>
								</section>

								<section className="space-y-6">
									<header>
										<h3 className="text-[18px] font-semibold">ການລົງທຶນ</h3>
										<p className="text-muted-foreground text-[14px]">
											ຂໍ້ມູນລົງທຶນຂອງວິສາຫະກິດ.
										</p>
										<Separator className="mt-6" />
									</header>

									{/* <div>
									<Label htmlFor="coreSectorId">
										ຂະແໜງການ <span className="text-red-500">*</span>
									</Label>
									<Select
										name="coreSectorId"
										onValueChange={(value) => {
											setFieldValue("coreSectorId", value);
										}}
										value={values.coreSectorId}
										required
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder="ເລືອກຂະແໜງການ" />
										</SelectTrigger>
										<SelectContent>
											{coreSector?.map((item) => (
												<SelectItem value={item.id} key={item.id}>
													{item?.coreSector}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div>
									<Label htmlFor="sectorId">
										ຂະແໜງການ (ອາກອນ) <span className="text-red-500">*</span>
									</Label>
									<Select
										name="sectorId"
										onValueChange={(value) => {
											setFieldValue("sectorId", value);
										}}
										value={values.sectorId}
										required
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder="ເລືອກຂະແໜງການ" />
										</SelectTrigger>
										<SelectContent>
											{sector?.map((item) => (
												<SelectItem value={item.id} key={item.id}>
													{item?.sector}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div> */}

									<div>
										<Label>ທຸລະກິດຫຼັກ</Label>
										<Field
											as={Input}
											name="coreBusiness"
											placeholder="ປ້ອນມີທຸລະກິດຫຼັກ..."
											type="text"
										/>
									</div>

									<div>
										<Label>ທຶນຈົດທະບຽນ</Label>
										<Field
											as={Input}
											name="registeredCapital"
											placeholder="ປ້ອນທຶນຈົດທະບຽນ..."
											type="text"
											inputMode="numeric"
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
												// Remove non-numeric characters
												const value = e.target.value.replace(/[^0-9]/g, "");
												// Format with commas
												const formattedValue = value
													? Number.parseInt(value).toLocaleString()
													: "";
												e.target.value = formattedValue;
												// Update the actual numeric value in Formik
												setFieldValue(
													"registeredCapital",
													value ? Number.parseInt(value) : 0,
												);
											}}
											value={
												values.registeredCapital
													? values.registeredCapital.toLocaleString()
													: ""
											}
										/>
									</div>
								</section>

								<div className="flex justify-end w-full gap-4">
									<Button
										onClick={() => navigate({ to: "..", replace: true })}
										variant={"outline"}
									>
										ຍົກເລີກ
									</Button>
									<Button type="submit" disabled={createSOEMutation.isPending}>
										{createSOEMutation.isPending ? (
											<Spinner className="text-white" />
										) : (
											<>
												<Save /> ບັນທຶກ
											</>
										)}
									</Button>
								</div>
							</>
						) : null}
					</form>
				)}
			</Formik>
		</>
	);
}
